// Scale bridge dashboard - readings and stats, held in one store.
//
// ─── Polling, where SBIS uses a WebSocket ──────────────────────────────
// SBIS pushes order events over /ws/orders because a busy shop generates a
// steady stream and a dashboard that lagged would be useless. This bridge
// produces one reading per weighing - minutes apart, not seconds - so a poll
// every few seconds is indistinguishable to the operator and removes a whole
// class of problem: no reconnect logic, no token in a URL, no server-side
// broadcast to get wrong.
//
// It is a real trade, not a free one: the refresh interval is the worst-case
// latency, and every client polls whether or not anything changed. If this ever
// runs on several sites at once, that is the point to lift SBIS's WS.

import { create } from 'zustand';
import { client, UnauthorizedError, type Session } from '../api/client';
import type { Reading, Stats } from '../types';

export const REFRESH_MS = 4000;

interface ReadingsState {
    readings: Reading[];
    stats: Stats | null;
    loading: boolean;
    /** Set on the first successful load, so the empty state can tell "nothing
     *  yet" apart from "not loaded yet" - they look identical otherwise. */
    loaded: boolean;
    error: string | null;
    unauthorized: boolean;
    lastRefreshAt: number | null;

    refresh: (session: Session) => Promise<void>;
    /** A reading that arrived over the live feed. */
    upsert: (reading: Reading) => void;
    /** Phase 4. Whether the live feed is connected; 'offline' also covers the
     *  view-key case, where there is no socket at all and the poll is the feed. */
    liveStatus: 'connecting' | 'live' | 'offline';
    setLiveStatus: (s: 'connecting' | 'live' | 'offline') => void;

    reset: () => void;
}

/**
 * Merge a hydration payload with what is already on screen — the store-merge rule.
 *
 * A WS reading can arrive BETWEEN the fetch being dispatched and its response
 * coming back. That reading is in local state and absent from the payload, and a
 * plain `set({ readings: fromServer })` silently drops a weighment that is already
 * on the operator's screen.
 *
 * So: the payload is authoritative for rows it knows about, and a local row missing
 * from it is KEPT if it is recent enough to be a plausible race arrival, and dropped
 * otherwise (it was probably deleted server-side while the fetch was in flight).
 */
const RACE_WINDOW_MS = 5 * 60 * 1000;

export function mergeReadings(local: Reading[], fromServer: Reading[]): Reading[] {
    const serverIds = new Set(fromServer.map((r) => r.id));
    const now = Date.now();
    const survivors = local.filter(
        (r) => !serverIds.has(r.id) && now - new Date(r.receivedAt).getTime() < RACE_WINDOW_MS,
    );
    return [...survivors, ...fromServer].sort(
        (a, b) => new Date(b.receivedAt).getTime() - new Date(a.receivedAt).getTime(),
    );
}

export const useReadingsStore = create<ReadingsState>((set) => ({
    readings: [],
    stats: null,
    loading: false,
    loaded: false,
    error: null,
    unauthorized: false,
    lastRefreshAt: null,
    liveStatus: 'offline',

    setLiveStatus: (liveStatus) => set({ liveStatus }),

    refresh: async (session) => {
        set({ loading: true });
        try {
            // Both in one round trip's worth of latency. They are read from the
            // same store on the server, so fetching them apart could show a KPI
            // strip and a table that disagree by one reading.
            const [bills, stats] = await Promise.all([
                client.bills(session),
                client.stats(session),
            ]);
            set((prev) => ({
                readings: mergeReadings(prev.readings, bills.readings),
                stats,
                loading: false,
                loaded: true,
                error: null,
                unauthorized: false,
                lastRefreshAt: Date.now(),
            }));
        } catch (err) {
            if (err instanceof UnauthorizedError) {
                set({ loading: false, unauthorized: true, error: null });
                return;
            }
            // Keep whatever was last loaded on screen. A transient network blip
            // should not blank a dashboard somebody is reading a weight off.
            set({
                loading: false,
                error: err instanceof Error ? err.message : 'Could not reach the receiver.',
            });
        }
    },

    upsert: (reading) =>
        set((prev) => {
            // Idempotent: the same reading can arrive twice - once over the socket
            // and once in the hydration that follows a reconnect - and must not
            // appear twice on screen.
            const without = prev.readings.filter((r) => r.id !== reading.id);
            const next = [reading, ...without];
            return {
                readings: next,
                // Deliberately NOT recomputed here. The stats endpoint aggregates
                // server-side over more rows than the client holds, so guessing at
                // them locally would make the KPI strip disagree with the table
                // until the next refresh. The socket bumps the list; the numbers
                // follow on the next poll.
                lastRefreshAt: prev.lastRefreshAt,
            };
        }),

    reset: () =>
        set({
            readings: [],
            stats: null,
            loading: false,
            loaded: false,
            error: null,
            unauthorized: false,
            lastRefreshAt: null,
        }),
}));

// ─── Selectors ─────────────────────────────────────────────────────────

export function selectTodayReadings(readings: Reading[]): Reading[] {
    const start = new Date();
    start.setHours(0, 0, 0, 0);
    return readings.filter((r) => +new Date(r.receivedAt) >= +start);
}

/**
 * Today's recorded weight, bucketed by hour.
 *
 * Only hours from the first reading to the current hour are returned. Padding the
 * whole 24 hours would draw a long run of empty bars every morning and make a
 * normal day look like an outage.
 */
export function selectWeightByHour(readings: Reading[]): Array<{ hour: string; kg: number; count: number }> {
    const today = selectTodayReadings(readings);
    if (today.length === 0) return [];

    const buckets = new Map<number, { kg: number; count: number }>();
    let earliest = 23;
    for (const r of today) {
        const h = new Date(r.receivedAt).getHours();
        if (h < earliest) earliest = h;
        const b = buckets.get(h) ?? { kg: 0, count: 0 };
        // An unparsed weight contributes to the count but not the total. Counting
        // it as 0 kg would understate the day, which is the same null-is-not-zero
        // rule the firmware and the printed bill follow.
        if (typeof r.totalNetWeightKg === 'number') b.kg += r.totalNetWeightKg;
        b.count += 1;
        buckets.set(h, b);
    }

    const out: Array<{ hour: string; kg: number; count: number }> = [];
    const lastHour = Math.max(new Date().getHours(), ...buckets.keys());
    for (let h = earliest; h <= lastHour; h++) {
        const b = buckets.get(h) ?? { kg: 0, count: 0 };
        out.push({ hour: `${String(h).padStart(2, '0')}:00`, kg: Math.round(b.kg * 1000) / 1000, count: b.count });
    }
    return out;
}
